import PropTypes from 'prop-types';
import { useState, useEffect, useContext, useCallback } from 'react';
import demosApi from '../../utils/demos.api';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Link,
  Drawer as MobileDrawer,
  Typography,
  Divider,
  Avatar,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListItem,
  Tooltip
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

// import sidebarConfig from './SidebarConfig';
import IconButton from '@mui/material/IconButton';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import peopleFill from '@iconify/icons-eva/people-fill';
import usageFill from '@iconify/icons-mdi/calendar-month';
import accountWrench from '@iconify/icons-mdi/account-wrench';
import officeBuilding from '@iconify/icons-mdi/office-building';
import folderUpload from '@iconify/icons-mdi/folder-upload';
import personFeedback28Filled from '@iconify/icons-fluent/person-feedback-28-filled';
import viewDashboard from '@iconify/icons-mdi/view-dashboard';
import accountTie from '@iconify/icons-mdi/account-tie';
import alphabets from '@iconify/icons-mdi/alphabetical-variant';
import chart from '@iconify/icons-mdi/chart-bar';
import pie from '@iconify/icons-mdi/chart-pie';
import chartTree from '@iconify/icons-mdi/chart-tree';
import taskIcon from '@iconify/icons-mdi/calendar-task';
import documentExport from '@iconify/icons-carbon/document-export';
import CarbonAlignVerticalBottom from '@iconify/icons-carbon/align-vertical-bottom';
import filesFill from '@iconify/icons-mdi/file-multiple';
import grid from '@iconify/icons-ic/round-grid-view';
import logout from '@iconify/icons-mdi/logout';
import roundCampaign from '@iconify/icons-ic/round-campaign';
import outlineDateRange from '@iconify/icons-ic/outline-date-range';
import dealIcon from '@iconify/icons-mdi/deal';
import formOutline from '@iconify/icons-mdi/form-outline';
import roundCategory from '@iconify/icons-ic/round-category';
import categoryPlus from '@iconify/icons-mdi/category-plus';
import searchIcon from '@iconify/icons-mdi/search';
import star12Filled from '@iconify/icons-fluent/star-12-filled';
import { ArrowBack } from '@mui/icons-material';
import reportData from '@iconify/icons-carbon/report-data';
import dataUsageIcon from '@iconify/icons-fluent/data-usage-20-filled';
import accountAdd from '@iconify/icons-line-md/account-add';
import phoneIcon from '@iconify/icons-mdi/phone-outline';
import scorecardIcon from '@iconify/icons-mdi/file-document-outline';
// components
import Scrollbar from '../../Components/Scrollbar';
import DashboardNavbar from './DashboardNavbar';
import NavSection from '../../Components/NavSection';
import { MHidden } from '../../Components/@material-extend';
import StorylaneContext from '../../Components/Context/StoryLaneContext';
// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_DRAWER_WIDTH = 80;

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: 'relative',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    borderRadius: 10,
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      display: 'none',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main
    }
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 15px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 15px)`
  }
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    marginTop: 65, // Adjusts the sidebar below the navbar
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': { ...openedMixin(theme), marginTop: 65, height: 'calc(100% - 65px)', overflow: 'hidden' }
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': { ...closedMixin(theme), marginTop: 65, height: 'calc(100% - 65px)', overflow: 'hidden' }
    })
  })
);

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { setVideoURL } = useContext(StorylaneContext);

  // -------------------------- Sidebar Menu ----------------------------
  const getIcon = (name) => <Icon icon={name} width={22} height={20} />;

  // Fetch approved demos for this user from the backend
  const [demoApplications, setDemoApplications] = useState([]);

  useEffect(() => {
    demosApi.getMyAccess()
      .then(({ data }) => {
        // Response: {success, data:[{id, app, title, url, description, is_active}]} — flat list
        const flat = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

        // Group flat demos by app name → [{app, demos:[{id,title,url,description}]}]
        const grouped = flat.reduce((acc, demo) => {
          const existing = acc.find((g) => g.app === demo.app);
          if (existing) {
            existing.demos.push(demo);
          } else {
            acc.push({ app: demo.app, demos: [demo] });
          }
          return acc;
        }, []);

        setDemoApplications(grouped);
        // Auto-load the first approved demo's URL into the video player
        if (grouped.length > 0 && grouped[0].demos?.length > 0) {
          setVideoURL(grouped[0].demos[0].url);
        }
      })
      .catch((err) => {
        console.error('Failed to load approved demos:', err);
      });
  }, [setVideoURL]);

  const sidebarConfigSuperAdmin = demoApplications.map((appData) => ({
    title: appData.app, // Main menu = Application name
    path: '/dashboard/demos',
    icon: getIcon(viewDashboard),
    children:
      appData.demos && appData.demos.length > 0
        ? appData.demos.map((demo) => ({
          title: demo.title,
          path: '/dashboard/demos', // Stay on the demo page; videoUrl sets the iframe
          icon: getIcon(chartTree),
          videoUrl: demo.url,
          demoId: demo.id
        }))
        : [] // No demos, no children
  }));

  // Admin-only nav items — appended after the demo list
  const stored = localStorage.getItem('UserDetails');
  const role = stored ? JSON.parse(stored)?.user?.role : null;
  const adminItems = role === 'admin' ? [
    {
      title: 'Demo Management',
      path: '/dashboard/demo-management',
      icon: getIcon(viewDashboard)
    },
    {
      title: 'Access Requests',
      path: '/dashboard/requests',
      icon: getIcon(peopleFill)
    },
    {
      title: 'User Management',
      path: '/dashboard/admin/users',
      icon: getIcon(accountWrench)
    }
  ] : [];

  const combined = [...sidebarConfigSuperAdmin, ...adminItems];
  const sidebarConfig = combined.length === 0
    ? [{ title: 'No demos approved yet', path: '/dashboard/demos', icon: getIcon(viewDashboard) }]
    : combined;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  // const [check, setcheck] = useState(0);

  const [sidebarOpen, setSidebar] = useState(isOpenSidebar);

  const handleLogout = () => {
    setVideoURL("");
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (sidebarOpen && typeof onCloseSidebar === 'function') {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {sidebarOpen ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              height: 50,
              width: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setSidebar(false)}
          >
            <ArrowBack sx={{ color: 'white', cursor: 'pointer' }} />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'primary.main',
            width: '100%',
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setSidebar(true)}
        >
          <IconButton>
            <Icon icon={menu2Fill} style={{ color: '#ffff' }} />
          </IconButton>
        </Box>
      )}
      <Divider sx={{ marginBottom: 2 }} />
      <Scrollbar
        sx={{
          flex: 1,
          overflowX: 'hidden',
          '& .simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: !sidebarOpen && 'center'
          }
        }}
      >
        <NavSection navConfig={sidebarConfig} sidebarCollapse={sidebarOpen} />
      </Scrollbar>
      <Divider />
      <Box onClick={handleLogout}>
        <ListItemStyle>
          {sidebarOpen ? (
            <ListItemIconStyle>
              <Icon icon={logout} width={22} height={22} />
            </ListItemIconStyle>
          ) : (
            <Tooltip title="Logout" followCursor placement="top">
              <ListItemIconStyle>
                <Icon icon={logout} width={22} height={22} />
              </ListItemIconStyle>
            </Tooltip>
          )}
          {sidebarOpen && <ListItemText disableTypography primary="Logout" />}
        </ListItemStyle>
      </Box>
    </Box>
  );

  return (
    <>
      <DashboardNavbar onOpenSidebar={() => setSidebar(true)} />
      <MHidden width="lgDown">
        <Drawer open={sidebarOpen} variant="permanent">
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgUp">
        {sidebarOpen && (
          <MobileDrawer
            open={sidebarOpen}
            onClose={() => setSidebar(false)}
            variant="persistant"
            PaperProps={{
              sx: {
                width: sidebarOpen === true ? DRAWER_WIDTH : COLLAPSE_DRAWER_WIDTH
              }
            }}
          >
            {renderContent}
          </MobileDrawer>
        )}
      </MHidden>
    </>
  );
}
